import {
	ArrowDownLeft,
	ArrowUpRight,
	CalendarClock,
	CheckSquare,
	Hash,
	Repeat,
	Timer,
	Wallet,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { formatUGX } from "@/lib/money";
import { describeRule } from "@/lib/recurrence";
import type { ParsedQuickAdd, QuickAddKind, WalletHint } from "@/lib/quick-add";
import { formatInZone } from "@/lib/time";
import { cn } from "@/lib/utils";

const kindMeta: Record<
	QuickAddKind,
	{ label: string; icon: typeof CheckSquare; className: string }
> = {
	task: { label: "Task", icon: CheckSquare, className: "" },
	expense: {
		label: "Expense",
		icon: ArrowUpRight,
		className: "text-money-out border-money-out/40",
	},
	income: { label: "Income", icon: ArrowDownLeft, className: "text-money-in border-money-in/40" },
};

const walletLabel: Record<WalletHint, string> = {
	cash: "Cash",
	momo: "MTN MoMo",
	airtel: "Airtel Money",
	bank: "Bank",
};

function formatDay(dueDate: string): string {
	// dueDate is already the Kampala day; noon UTC keeps it on that day in Kampala (UTC+3).
	return formatInZone(new Date(`${dueDate}T12:00:00Z`), "EEE d MMM");
}

/** What quick-add will create, shown before saving (project-description.md §10). */
/** `target` names where a money row will land, e.g. "MTN MoMo · Food". */
export function QuickAddPreview({
	parsed,
	target,
}: {
	parsed: ParsedQuickAdd | null;
	target?: string;
}) {
	if (!parsed) return <div className="min-h-6" aria-live="polite" />;
	const meta = kindMeta[parsed.kind];

	return (
		<div className="grid min-h-6 gap-2 text-sm" aria-live="polite" data-testid="quick-add-preview">
			<div className="flex flex-wrap items-center gap-2">
				<Badge variant="outline" className={cn("gap-1", meta.className)}>
					<meta.icon className="size-3" />
					{meta.label}
				</Badge>

				{parsed.kind === "task" ? (
					<>
						{parsed.dueDate && (
							<Badge variant="secondary" className="gap-1">
								<CalendarClock className="size-3" />
								{parsed.dueAt
									? formatInZone(parsed.dueAt, "EEE d MMM, HH:mm")
									: formatDay(parsed.dueDate)}
							</Badge>
						)}
						{parsed.recurrence && (
							<Badge variant="secondary" className="gap-1">
								<Repeat className="size-3" />
								{describeRule(parsed.recurrence)}
							</Badge>
						)}
						{parsed.estimateMin !== null && (
							<Badge variant="secondary" className="gap-1">
								<Timer className="size-3" />
								{parsed.estimateMin} min
							</Badge>
						)}
					</>
				) : (
					<>
						<span
							className={cn(
								"font-semibold tabular-nums",
								parsed.kind === "expense" ? "text-money-out" : "text-money-in",
							)}
						>
							{formatUGX(parsed.amount)}
						</span>
						{parsed.wallet && (
							<Badge variant="secondary" className="gap-1">
								<Wallet className="size-3" />
								{walletLabel[parsed.wallet]}
							</Badge>
						)}
						{parsed.occurredAt && (
							<Badge variant="secondary" className="gap-1">
								<CalendarClock className="size-3" />
								{formatInZone(parsed.occurredAt, "EEE d MMM")}
							</Badge>
						)}
					</>
				)}

				{parsed.tags.map((tag) => (
					<Badge key={tag} variant="outline" className="gap-0.5">
						<Hash className="size-3" />
						{tag}
					</Badge>
				))}
			</div>

			{parsed.kind === "task" ? (
				<p className={cn("truncate", !parsed.title && "text-muted-foreground")}>
					{parsed.title || "Add a title"}
				</p>
			) : (
				<p className="text-muted-foreground truncate">
					{parsed.note || "No note"}
					{target && ` · ${target}`}
				</p>
			)}
		</div>
	);
}
